import { ReactNode } from 'react'
import { ItemDataType } from 'rsuite/esm/internals/types'
import { GeckoAddress } from 'types'
import { parseDateAndSetToEST } from 'utils/parsingHelpers'
import { ZodEffects, ZodTypeAny } from 'zod'

import { GeckoMeasurementUnit } from './measurement/geckoMeasurementUnit'

export enum UnitMagnitude {
  FeetOrMeters,
  InchesOrMillimeters,
  FahrenheitOrCelsius,
  PsiOrMPa,
  Degrees,
}

export const unitMagnitudeLabels: Record<UnitMagnitude, string> = {
  [UnitMagnitude.FeetOrMeters]: 'Feet/Meters',
  [UnitMagnitude.InchesOrMillimeters]: 'Inches/Millimeters',
  [UnitMagnitude.FahrenheitOrCelsius]: 'Fahrenheit/Celsius',
  [UnitMagnitude.PsiOrMPa]: 'PSI/MPa',
  [UnitMagnitude.Degrees]: 'Degrees',
}

export interface SortTableByField {
  fieldName: string
  order: string
}

export enum InputType {
  Number = 'number',
  Text = 'text',
  TextArea = 'text_area',
  Dropdown = 'dropdown',
  Date = 'date',
  Coordinates = 'coordinates',
  Address = 'address',
  MultiSelect = 'multi_select',
  TextLink = 'text_link',
  Combobox = 'combobox',
}

export enum ExternalUserFieldAccess {
  ReadOnly,
  ReadWrite,
  NoAccess,
}

export type DropdownOption = {
  value: string
  label: ReactNode
  disabled?: boolean
  tooltip?: string
  logo?: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

export type FormFieldDependency = {
  name: string
  has: string | number
}

export type FormField = {
  dataKey: string
  label?: string
  value: string | number | string[] | undefined
  unit_magnitude: UnitMagnitude | GeckoMeasurementUnit | null
  externalUserAccess: ExternalUserFieldAccess
  initialValue?: string | number
  options?: DropdownOption[]
  validationRule: ZodEffects<ZodTypeAny, unknown, unknown>
  inputType: InputType
  isNeededForDerivedLayer: boolean
  dependencies?: FormFieldDependency[]
  disabled?: boolean
  renderMenuItem?: (
    label: React.ReactNode,
    item: ItemDataType
  ) => React.ReactNode
  formatAddressValueAsString?: boolean
}

const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

const isNumber = (value: unknown): value is number => {
  return typeof value === 'number'
}

function isStringNumber(value: unknown): value is string {
  return value !== undefined && !isNaN(Number(value))
}

const isAddressObject = (value: unknown): value is GeckoAddress => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'address_line_1' in value &&
    'address_line_2' in value &&
    'city' in value &&
    'region' in value &&
    'postal_code' in value &&
    'country' in value
  )
}

export const getArrayFieldByDataKey = (
  formFields: FormField[],
  dataKey: string
): string[] | undefined => {
  const field = getFormFieldByDataKey(formFields, dataKey)
  if (field && Array.isArray(field.value)) {
    return field.value
  }
  return undefined
}

export const getStringFieldByDataKey = (
  formFields: FormField[],
  dataKey: string
) => {
  const field = getFormFieldByDataKey(formFields, dataKey)
  if (field && isString(field.value)) {
    return field.value
  }
  return undefined
}

export const getNumberFieldByDataKey = (
  formFields: FormField[],
  dataKey: string
) => {
  const field = getFormFieldByDataKey(formFields, dataKey)
  if (field && (isNumber(field.value) || isStringNumber(field.value))) {
    return Number(field.value)
  }
  return undefined
}

export const getDateFieldByDataKey = (
  formFields: FormField[],
  dataKey: string
) => {
  const field = getFormFieldByDataKey(formFields, dataKey)
  if (field) {
    return parseDateAndSetToEST(field.value)
  }
  return undefined
}

export const getGeckoAddressByDataKey = (
  formFields: FormField[],
  dataKey: string
): GeckoAddress | undefined => {
  const field = getFormFieldByDataKey(formFields, dataKey)
  if (field && isAddressObject(field.value)) {
    return field.value
  }
  return undefined
}

export const getFormFieldByDataKey = (
  formFields: FormField[],
  dataKey: string
): FormField | undefined => {
  return formFields.find((field) => field.dataKey === dataKey)
}

export const formatAddress = (
  address: GeckoAddress | undefined
): string | undefined => {
  if (!address) return undefined

  const { address_line_1, address_line_2, city, region, postal_code } = address
  const addressParts: string[] = []

  if (address_line_1?.trim()) addressParts.push(address_line_1.trim())
  if (address_line_2?.trim()) addressParts.push(address_line_2.trim())

  const cityPart = city?.trim()
  const regionPart = region?.trim()
  const postalPart = postal_code?.trim()

  if (cityPart && regionPart && postalPart) {
    addressParts.push(cityPart)
    addressParts.push(`${regionPart} ${postalPart}`)
  } else if (cityPart && regionPart) {
    addressParts.push(cityPart)
    addressParts.push(regionPart)
  } else if (cityPart && postalPart) {
    addressParts.push(`${cityPart} ${postalPart}`)
  } else if (cityPart) {
    addressParts.push(cityPart)
  } else if (regionPart && postalPart) {
    addressParts.push(`${regionPart} ${postalPart}`)
  } else if (regionPart) {
    addressParts.push(regionPart)
  } else if (postalPart) {
    addressParts.push(postalPart)
  }

  return addressParts.length > 0 ? addressParts.join(', ') : ''
}
